/**
 * tokenStorage — unified JWT persistence for web + Capacitor native.
 *
 * Perché esiste:
 *   Su iOS WKWebView (Capacitor) cookie e localStorage possono essere
 *   azzerati dal sistema in scenari di low-memory o quando l'app viene
 *   messa in background a lungo. Il risultato è che l'utente, riaprendo
 *   l'app, deve rifare il login anche se il JWT non è scaduto.
 *
 *   La soluzione robusta su iOS è usare `@capacitor/preferences` che
 *   scrive su UserDefaults (storage nativo iOS) — garantito persistente
 *   tra restart, background e aggiornamenti dell'app.
 *
 * Strategia:
 *   - Memoria volatile (sync) come fonte primaria per l'interceptor axios
 *   - Cookie + localStorage (sync) come fonte secondaria + compat con i
 *     componenti esistenti che leggono `Cookies.get("vista_jwt")` o
 *     `localStorage.getItem("vista_jwt")` direttamente
 *   - Capacitor Preferences (async) come fonte autoritativa su iOS/Android
 *
 * Lifecycle:
 *   1. Boot app  → `hydrateToken()` (async) legge da Preferences > cookie
 *                  > localStorage e popola la memoria + mirror cross-storage
 *   2. Login     → `setToken(t)` scrive ovunque (sync per cookie/local/mem,
 *                  fire-and-forget su Preferences)
 *   3. Logout    → `clearToken()` pulisce ovunque
 *   4. Interceptor axios → `getTokenSync()` per allegare Bearer header
 */

import Cookies from "js-cookie";
import { Capacitor } from "@capacitor/core";

const STORAGE_KEY = "vista_jwt";
const IS_BROWSER = typeof window !== "undefined";
const IS_HTTPS = IS_BROWSER && window.location?.protocol === "https:";

// Mirror sincrono usato dall'interceptor axios (che è sync).
let _memToken = null;

// True se siamo già passati per hydrateToken() almeno una volta.
let _hydrated = false;

/** True quando giriamo dentro Capacitor (nativo iOS/Android). */
function _isNative() {
  try {
    return !!Capacitor?.isNativePlatform?.();
  } catch {
    return false;
  }
}

async function _nativeGet() {
  if (!_isNative()) return null;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    return value || null;
  } catch {
    return null;
  }
}

async function _nativeSet(token) {
  if (!_isNative()) return;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key: STORAGE_KEY, value: token });
  } catch { /* plugin assente o WebView in shutdown */ }
}

async function _nativeRemove() {
  if (!_isNative()) return;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.remove({ key: STORAGE_KEY });
  } catch { /* no-op */ }
}

function _cookieSet(token) {
  try {
    Cookies.set(STORAGE_KEY, token, {
      expires: 90, // iter v32 r2 — allineato a JWT TTL backend (90 giorni)
      sameSite: "lax",
      secure: IS_HTTPS,
      path: "/",
    });
  } catch { /* no-op */ }
}

function _localSet(token) {
  try { window.localStorage.setItem(STORAGE_KEY, token); } catch { /* private mode */ }
}

function _cookieGet() {
  try { return Cookies.get(STORAGE_KEY) || null; } catch { return null; }
}

function _localGet() {
  try { return window.localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
}

function _cookieRemove() {
  try { Cookies.remove(STORAGE_KEY, { path: "/" }); } catch { /* no-op */ }
}

function _localRemove() {
  try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* no-op */ }
}

/**
 * Carica il token dalla fonte più persistente disponibile e lo riscrive
 * in tutti gli storage in modo che ogni lettura successiva (anche quelle
 * legacy sparse nei componenti) lo veda.
 *
 * Ordine di priorità (da più persistente a meno):
 *   1. Capacitor Preferences (solo native): sopravvive a tutto
 *   2. Cookie + localStorage: fallback web e per chi è loggato già
 *
 * Su native, se Preferences ha un token ma cookie/localStorage no, li
 * riscriviamo così i componenti legacy che fanno `Cookies.get(...)`
 * continuano a funzionare senza essere migrati uno per uno.
 *
 * @returns {Promise<string|null>} il token corrente, o null se non loggato.
 */
export async function hydrateToken() {
  if (!IS_BROWSER) return null;
  // Ordine di lettura: preferiamo cookie + local PRIMA (cache rapida, no I/O),
  // poi controlliamo Preferences SOLO se i primi due sono vuoti — questo
  // copre il caso WKWebView che ha azzerato cookie+localStorage ma le
  // UserDefaults sono intatte.
  const fromCookie = _cookieGet();
  const fromLocal = _localGet();
  let token = fromCookie || fromLocal;

  if (!token) {
    const fromNative = await _nativeGet();
    if (fromNative) {
      token = fromNative;
      // Re-mirror su cookie + localStorage così il resto del codice
      // (interceptor axios, componenti legacy con Cookies.get) lo vede.
      _cookieSet(token);
      _localSet(token);
    }
  } else {
    // Cookie/local hanno un token: assicuriamoci che anche Preferences
    // lo abbia (caso: utente loggato prima dell'introduzione di questo
    // storage, oppure cookie sopravvissuto ma Preferences cancellato).
    _nativeSet(token); // fire-and-forget
    // Sincronizza anche cookie ↔ localStorage se mancante in uno dei due
    if (!fromCookie && fromLocal) _cookieSet(fromLocal);
    if (!fromLocal && fromCookie) _localSet(fromCookie);
  }

  _memToken = token || null;
  _hydrated = true;
  return _memToken;
}

/** Ritorna il token in memoria (sync). Usato dall'interceptor axios. */
export function getTokenSync() {
  if (_memToken) return _memToken;
  // Fallback rapido se hydrateToken() non è ancora stato chiamato
  // (es. una chiamata axios partita molto presto durante il boot).
  if (!IS_BROWSER) return null;
  const t = _cookieGet() || _localGet();
  if (t) _memToken = t;
  return _memToken;
}

/** True se hydrateToken() è già stato eseguito almeno una volta. */
export function isHydrated() {
  return _hydrated;
}

/**
 * Salva il token in TUTTI gli storage disponibili.
 *  - sync: memoria + cookie + localStorage (visibili immediatamente)
 *  - async fire-and-forget: Capacitor Preferences (persistenza nativa)
 */
export function setToken(token) {
  if (!token) return;
  _memToken = token;
  if (!IS_BROWSER) return;
  _cookieSet(token);
  _localSet(token);
  // Native write non bloccante: l'app non deve aspettare iOS UserDefaults.
  _nativeSet(token).catch(() => {});
}

/** Cancella il token da TUTTI gli storage. */
export function clearToken() {
  _memToken = null;
  if (!IS_BROWSER) return;
  _cookieRemove();
  _localRemove();
  _nativeRemove().catch(() => {});
}
